import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { gql } from '../../graphql/client';

const Q_LIST_CONTENT = /* GraphQL */ `
  query ListContent($appId: uuid!) {
    external_content(where: { frontend_app: { _eq: $appId } }, order_by: { alias: asc }) {
      id alias burrow_content_text
    }
  }
`;

const M_UPSERT_CONTENT = /* GraphQL */ `
  mutation UpsertContent($appId: uuid!, $alias: String!, $text: String!) {
    insert_external_content_one(
      object: { frontend_app: $appId, alias: $alias, burrow_content_text: $text }
      on_conflict: { constraint: external_content_frontend_app_alias_key, update_columns: [burrow_content_text] }
    ) { id alias burrow_content_text }
  }
`;

const M_DELETE_CONTENT = /* GraphQL */ `
  mutation DeleteContent($id: uuid!) {
    delete_external_content_by_pk(id: $id) { id }
  }
`;

interface ContentRow {
  id: string;
  alias: string;
  burrow_content_text: string | null;
}

export function ContentEditor({ appId }: { appId: string }) {
  const queryClient = useQueryClient();
  const list = useQuery({
    queryKey: ['content', appId],
    queryFn: () => gql<{ external_content: ContentRow[] }>(Q_LIST_CONTENT, { appId }),
  });
  const [alias, setAlias] = useState('');
  const [text, setText] = useState('');

  const upsert = useMutation({
    mutationFn: () => gql(M_UPSERT_CONTENT, { appId, alias: alias.trim(), text }),
    onSuccess() {
      setAlias('');
      setText('');
      queryClient.invalidateQueries({ queryKey: ['content', appId] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => gql(M_DELETE_CONTENT, { id }),
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['content', appId] });
    },
  });

  return (
    <div style={styles.section}>
      <h3 style={styles.heading}>Content</h3>
      <p style={styles.hint}>Reference these from views as <code>{'${content.alias}'}</code>.</p>

      <div style={styles.list}>
        {list.data?.external_content.map((row) => (
          <div key={row.id} style={styles.row}>
            <span style={styles.alias}>{row.alias}</span>
            <span style={styles.text}>{row.burrow_content_text}</span>
            <button onClick={() => del.mutate(row.id)} style={styles.delBtn}>×</button>
          </div>
        ))}
        {list.data?.external_content.length === 0 && <p style={styles.empty}>No content yet.</p>}
      </div>

      <div style={styles.add}>
        <input value={alias} onChange={(e) => setAlias(e.target.value)} placeholder="alias" style={styles.input} />
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Text content" style={{ ...styles.input, flex: 1 }} />
        <button
          onClick={() => alias && text && upsert.mutate()}
          disabled={!alias || !text || upsert.isPending}
          style={styles.addBtn}
        >
          Add
        </button>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  section: { padding: 16 },
  heading: { fontSize: 13, fontWeight: 600, color: '#0F172A', margin: '0 0 8px' },
  hint: { fontSize: 11, color: '#94A3B8', margin: '0 0 12px' },
  list: { marginBottom: 12 },
  row: { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 0', borderBottom: '1px solid #F1F5F9' },
  alias: { width: 90, fontSize: 11, fontWeight: 600, color: '#4F46E5', fontFamily: 'monospace' },
  text: { flex: 1, fontSize: 12, color: '#475569' },
  delBtn: { width: 20, height: 20, padding: 0, fontSize: 14, color: '#94A3B8', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' },
  empty: { fontSize: 12, color: '#94A3B8' },
  add: { display: 'flex', gap: 4 },
  input: { padding: '6px 8px', fontSize: 12, border: '1px solid #CBD5E1', borderRadius: 4 },
  addBtn: { padding: '6px 10px', fontSize: 12, fontWeight: 600, color: '#fff', backgroundColor: '#4F46E5', border: 'none', borderRadius: 4, cursor: 'pointer' },
};
